import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const COOKIE_KEY = "tf_cookie_consent_v1";

export default function CookieBanner({ openSettings, onCloseSettings }) {
  const initial = (() => {
    if (typeof window === "undefined") return { hasStored: false };
    return { hasStored: !!localStorage.getItem(COOKIE_KEY) };
  })();

  const [showBanner, setShowBanner] = useState(!initial.hasStored);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (openSettings) setShowModal(true);
  }, [openSettings]);

  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setShowModal(false);
        onCloseSettings?.();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, onCloseSettings]);

  const acceptNecessary = () => {
    localStorage.setItem(
      COOKIE_KEY,
      JSON.stringify({ necessary: true, ts: Date.now() })
    );
    setShowBanner(false);
    setShowModal(false);
    onCloseSettings?.();
  };

  return (
    <>
      {showBanner && (
        <div className="cookie-banner reveal is-visible" data-testid="cookie-banner">
          <p className="text-[15px] leading-relaxed" style={{ color: "var(--ink)" }}>
            Diese Website verwendet ausschließlich technisch notwendige Cookies.
          </p>
          <p className="text-xs mt-2" style={{ color: "var(--ink-mute)" }}>
            Es findet kein Tracking statt. Weitere Informationen in der{" "}
            <a href="#datenschutz" className="soft-link">Datenschutzerklärung</a>.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              data-testid="cookie-accept-all"
              onClick={acceptNecessary}
              className="btn-primary !py-2.5 !px-4 !text-sm"
            >
              Einverstanden
            </button>
            <button
              data-testid="cookie-customize"
              onClick={() => setShowModal(true)}
              className="soft-link text-sm ml-1"
            >
              Anpassen
            </button>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          data-testid="cookie-modal" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => { setShowModal(false); onCloseSettings?.(); }} />
          <div className="relative w-full max-w-lg bg-white rounded-3xl p-7 lg:p-8 border shadow-2xl"
            style={{ borderColor: "var(--line)" }}>
            <button
              data-testid="cookie-modal-close"
              onClick={() => { setShowModal(false); onCloseSettings?.(); }}
              aria-label="Schließen"
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[color:var(--cream-deep)] transition-colors"
            >
              <X size={18} />
            </button>
            <h3 className="font-serif text-2xl" style={{ color: "var(--ink)" }}>Cookie-Einstellungen</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              Diese Website verwendet ausschließlich technisch notwendige Cookies bzw. einen
              Eintrag im lokalen Speicher (localStorage), um Ihre Auswahl zu merken. Es werden
              keine Tracking-, Analyse- oder Marketing-Cookies eingesetzt.
            </p>

            <div className="mt-6 space-y-4">
              <Row
                label="Unbedingt erforderlich"
                desc="Erforderlich für die Grundfunktionen der Website und Ihre Cookie-Auswahl."
                checked
                disabled
                testid="cookie-toggle-necessary"
              />
              <Row
                label="Funktionell"
                desc="Aktuell keine Cookies in dieser Kategorie."
                empty
                testid="cookie-toggle-functional"
              />
              <Row
                label="Analyse / Performance"
                desc="Aktuell keine Cookies in dieser Kategorie."
                empty
                testid="cookie-toggle-analytics"
              />
              <Row
                label="Marketing / Third Party"
                desc="Aktuell keine Cookies in dieser Kategorie."
                empty
                testid="cookie-toggle-marketing"
              />
            </div>

            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button onClick={acceptNecessary} data-testid="cookie-save" className="btn-primary flex-1">
                Auswahl speichern
              </button>
              <button
                onClick={() => { setShowModal(false); onCloseSettings?.(); }}
                data-testid="cookie-modal-secondary-close"
                className="btn-secondary flex-1"
              >
                Schließen
              </button>
            </div>

            <p className="mt-5 text-xs" style={{ color: "var(--ink-mute)" }}>
              Weitere Details in der{" "}
              <a href="#datenschutz" onClick={() => { setShowModal(false); onCloseSettings?.(); }} className="soft-link">
                Datenschutzerklärung
              </a>{" "}
              und im{" "}
              <a href="#impressum" onClick={() => { setShowModal(false); onCloseSettings?.(); }} className="soft-link">
                Impressum
              </a>.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

const Row = ({ label, desc, checked, onChange, disabled, empty, testid }) => (
  <div className="flex items-start justify-between gap-4 p-4 rounded-2xl"
    style={{ background: "var(--cream-deep)" }}>
    <div>
      <p className="text-[15px] font-medium" style={{ color: "var(--ink)" }}>{label}</p>
      <p className="text-xs mt-1 max-w-[34ch]" style={{ color: "var(--ink-soft)" }}>{desc}</p>
    </div>
    {empty ? (
      <span
        data-testid={testid}
        className="text-[10px] tracking-[0.2em] uppercase shrink-0"
        style={{ color: "var(--ink-mute)" }}
      >
        Keine Cookies
      </span>
    ) : (
      <Switch
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        data-testid={testid}
      />
    )}
  </div>
);
